/**
 * usePermissions — Crew member permissions context
 *
 * Fetches the logged-in user's row from crew_members on mount.
 * Supports owner-only "View As" impersonation for testing permissions:
 *   - Owner picks a crew member from Manage Crew ("View As X")
 *   - Selection is stored in sessionStorage (viewAsEmail)
 *   - Subsequent renders load THAT crew member's row instead of the owner's
 *   - `realIsOwner` always reflects the actual signed-in user so we can
 *     still show the exit banner and allow un-impersonating.
 *
 * Provides:
 *   { role, permissions, isOwner, isAdmin, hasPermission, crewMember,
 *     jobberEmployeeName, realIsOwner, viewAsEmail, setViewAsEmail }
 */

import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../lib/supabase";

const PermissionsContext = createContext(null);
export const usePermissions = () => useContext(PermissionsContext);

const VIEW_AS_KEY = "elecbid.viewAsEmail";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Resolve a crew member by ANY of their login addresses — the primary
// crew_members.email OR an entry in alt_emails (20260805_crew_alt_login_emails).
// alt_emails lets one person sign in from a second account (e.g. a personal Google
// account on a personal phone) without switching accounts, and land on the SAME
// crew record — same role, permissions, bonus and timesheets.
//
// maybeSingle() stays safe because crew_alt_emails_validate() rejects, at write
// time, any alt that collides with another row's primary or alt address.
async function fetchCrewMember(email) {
  const delays = [1000, 2000, 4000];
  const e = String(email).toLowerCase();
  let lastError = null;
  for (let i = 0; i <= delays.length; i++) {
    if (i > 0) await sleep(delays[i - 1]);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 5000);
    try {
      let { data, error } = await supabase
        .from("crew_members")
        .select("*")
        .or(`email.eq.${e},alt_emails.cs.{${e}}`)
        .maybeSingle()
        .abortSignal(controller.signal);

      // If alt_emails does not exist — this bundle deployed ahead of its migration,
      // or the migration was reverted — PostgREST 400s with 42703 (undefined column).
      // Retrying on the primary email is what keeps that from locking EVERY crew
      // member out of the app; it restores exactly the pre-alias behaviour.
      // 42703 is the undefined-column SQLSTATE; the message test also catches a
      // PostgREST-level filter parse error, so the retry can't hinge on which layer
      // rejects it.
      if (error && (error.code === "42703" || /alt_emails/i.test(error.message || ""))) {
        console.warn("[usePermissions] alt_emails missing — falling back to primary email");
        ({ data, error } = await supabase
          .from("crew_members")
          .select("*")
          .eq("email", e)
          .maybeSingle());
      }

      clearTimeout(timer);
      if (!error) return { data, error: null };
      lastError = error;
    } catch (err) {
      clearTimeout(timer);
      lastError = err;
    }
    console.warn(`[usePermissions] crew_members attempt ${i + 1} failed:`, lastError?.message);
  }
  return { data: null, error: lastError };
}

// Stamp the sign-in on the crew member's own row.
//
// ⚠️ This MUST be awaited (or .then()-ed). A postgrest-js builder is a THENABLE,
// not an eager promise: PostgrestBuilder.then() is where the fetch() is actually
// issued (node_modules/@supabase/postgrest-js). A bare
//     supabase.from("crew_members").update({...}).eq("id", id);
// therefore builds a request and drops it — nothing leaves the browser. That is
// why last_login_at read NULL for all 12 crew members, owner included, despite
// this line having "run" on every login since it was added.
//
// ⚠️ .select("id") is not cosmetic. RLS filters rows OUT of an UPDATE rather than
// rejecting it, so a crew_update policy miss returns 204 / zero rows with
// error === null — awaiting alone would still look like success. The returned row
// is the only thing that distinguishes "stamped" from "silently dropped". A
// WITH CHECK violation or a crew_members_guard() rejection does surface as `error`.
//
// Never allowed to block sign-in: this is telemetry, and a failure here must not
// cost anyone access. It reports loudly to the console and returns.
async function stampLastLogin(id) {
  const { data, error } = await supabase
    .from("crew_members")
    .update({ last_login_at: new Date().toISOString() })
    .eq("id", id)
    .select("id");

  if (error) {
    console.error("[usePermissions] last_login_at write failed:", error.message, error.code);
  } else if (!data?.length) {
    // Expected on the user_metadata.crew_member_id fallback path only: crew_update
    // requires the row's email to match the JWT (via current_crew_email(), which
    // also resolves alt_emails), and that path deliberately loads a row whose email
    // is NOT the caller's. Any other occurrence is a real policy regression.
    console.warn(
      `[usePermissions] last_login_at not stamped for crew_members.id=${id} — ` +
        "RLS matched no row (crew_update requires the row's email to match the JWT)."
    );
  }
}

function readViewAs() {
  if (typeof window === "undefined") return "";
  try { return window.sessionStorage.getItem(VIEW_AS_KEY) || ""; } catch { return ""; }
}
function writeViewAs(email) {
  if (typeof window === "undefined") return;
  try {
    if (email) window.sessionStorage.setItem(VIEW_AS_KEY, email);
    else window.sessionStorage.removeItem(VIEW_AS_KEY);
  } catch { /* noop */ }
}

// ── Loading spinner (matches app style) ──────────────────────────────────────
function Spinner() {
  return (
    <div style={{
      minHeight: "100vh", background: "#0d0f12",
      display: "flex", alignItems: "center", justifyContent: "center"
    }}>
      <div style={{
        width: 36, height: 36,
        border: "3px solid rgba(165,206,58,0.2)",
        borderTopColor: "#a5ce3a",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite"
      }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ── Access denied screen ─────────────────────────────────────────────────────
function AccessDenied({ signOut, reason }) {
  if (reason === "query_error") {
    return (
      <div style={{
        minHeight: "100vh", background: "#0d0f12",
        display: "flex", alignItems: "center", justifyContent: "center",
        fontFamily: "'Roboto', sans-serif"
      }}>
        <div style={{ textAlign: "center", padding: 32, maxWidth: 420 }}>
          <div style={{
            fontSize: 52, fontFamily: "'Bebas Neue', sans-serif",
            color: "#a5ce3a", letterSpacing: "0.04em", marginBottom: 12,
            lineHeight: 1
          }}>
            Connection Error
          </div>
          <div style={{ color: "#8892a4", fontSize: 15, lineHeight: 1.7, marginBottom: 28 }}>
            Couldn't verify your access — the database may be temporarily unreachable.
            Try refreshing the page.
          </div>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 28px", borderRadius: 8,
              background: "#a5ce3a", color: "#0d0f12",
              fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer",
              fontFamily: "'Roboto', sans-serif", marginRight: 12
            }}
          >
            Reload
          </button>
          <button
            onClick={signOut}
            style={{
              padding: "10px 28px", borderRadius: 8,
              background: "transparent", color: "#8892a4",
              fontWeight: 700, fontSize: 14,
              border: "1px solid #2a2f38", cursor: "pointer",
              fontFamily: "'Roboto', sans-serif"
            }}
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  const message = reason === "deactivated"
    ? "Your account has been deactivated. Contact your administrator."
    : "You don't have access to this app. Ask your administrator for an invite.";
  return (
    <div style={{
      minHeight: "100vh", background: "#0d0f12",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontFamily: "'Roboto', sans-serif"
    }}>
      <div style={{ textAlign: "center", padding: 32, maxWidth: 420 }}>
        <div style={{
          fontSize: 52, fontFamily: "'Bebas Neue', sans-serif",
          color: "#a5ce3a", letterSpacing: "0.04em", marginBottom: 12,
          lineHeight: 1
        }}>
          Access Denied
        </div>
        <div style={{ color: "#8892a4", fontSize: 15, lineHeight: 1.7, marginBottom: 28 }}>
          {message}
        </div>
        <button
          onClick={signOut}
          style={{
            padding: "10px 28px", borderRadius: 8,
            background: "#a5ce3a", color: "#0d0f12",
            fontWeight: 700, fontSize: 14, border: "none", cursor: "pointer",
            fontFamily: "'Roboto', sans-serif"
          }}
        >
          Sign Out
        </button>
      </div>
    </div>
  );
}

// ── Provider ─────────────────────────────────────────────────────────────────
export function PermissionsProvider({ session, signOut, children }) {
  // undefined = still loading, null = not found / inactive
  const [realCrewMember, setRealCrewMember] = useState(undefined);
  const [deniedReason, setDeniedReason] = useState(null); // "deactivated" | "not_found"
  const [viewAsMember, setViewAsMember] = useState(null);
  const [viewAsEmail, setViewAsEmailState] = useState(() => readViewAs());
  // Per-company feature flags (null = not loaded yet → fail-open).
  const [companyFeatures, setCompanyFeatures] = useState(null);

  // ── Load the REAL signed-in crew member ────────────────────────────────────
  // Resolve the signed-in user's crew_members row.
  //
  // EMAIL FIRST, metadata only as a fallback — the order matters and used to be the
  // other way round. user_metadata is USER-WRITABLE (supabase.auth.updateUser({data}))
  // and this hook trusted crew_member_id AHEAD of the email lookup, so any crew member
  // could point it at the owner's row id and load the owner's row — role: "owner",
  // every permission true. That is a privilege escalation with no database write.
  //
  // Checked before changing: all 4 metadata-linked auth users point at their OWN row
  // (same email), and there are ZERO cross-email links in prod — so email-first returns
  // the identical row for everyone today and nothing changes for them.
  //
  // The fallback is kept so ManageCrew's "link a test email to an existing member"
  // flow still works: that account has no crew_members row of its own, so the email
  // lookup misses and the metadata link resolves it. A real crew member always HAS a
  // row, so their metadata is never consulted — the escalation is closed for exactly
  // the people who could have used it. They cannot delete their own row to force the
  // fallback either: DELETE is admin-only since
  // 20260716_crew_members_privilege_lockdown.sql.
  //
  // This was already defanged server-side (api/* re-checks the real JWT and RLS keys
  // off the real email, so a spoofed owner UI rendered without data) — this closes the
  // read/UI half.
  useEffect(() => {
    if (!session?.user) {
      setDeniedReason("not_found");
      setRealCrewMember(null);
      return;
    }

    const crewMemberId = session.user.user_metadata?.crew_member_id;
    const email = session.user.email?.toLowerCase();

    if (!crewMemberId && !email) {
      setDeniedReason("not_found");
      setRealCrewMember(null);
      return;
    }

    const lookupPromise = email
      ? fetchCrewMember(email).then((res) => {
          // Only fall back to the (user-writable) metadata link when this email owns
          // no row at all.
          if (!res.error && !res.data && crewMemberId) {
            return supabase.from("crew_members").select("*").eq("id", crewMemberId).maybeSingle();
          }
          return res;
        })
      : supabase.from("crew_members").select("*").eq("id", crewMemberId).maybeSingle();

    Promise.resolve(lookupPromise).then(({ data, error }) => {
      if (error) {
        console.error("[usePermissions] crew_members query failed:", error.message, error.code);
        setDeniedReason("query_error");
        setRealCrewMember(null);
      } else if (!data) {
        setDeniedReason("not_found");
        setRealCrewMember(null);
      } else if (!data.is_active) {
        setDeniedReason("deactivated");
        setRealCrewMember(null);
      } else {
        setDeniedReason(null);
        setRealCrewMember(data);
        // Deliberately not awaited — sign-in must not wait on telemetry — but the
        // promise is consumed, which is what actually issues the request. The catch
        // is belt-and-braces: postgrest-js maps fetch failures into `error` rather
        // than rejecting, so this should never fire.
        stampLastLogin(data.id).catch((err) =>
          console.error("[usePermissions] last_login_at write threw:", err?.message)
        );
      }
    });
    // session.user.id uniquely identifies the user; metadata is baked into the JWT
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  // ── Load the VIEW-AS crew member when impersonating ───────────────────────
  useEffect(() => {
    if (!viewAsEmail || realCrewMember?.role !== "owner") {
      setViewAsMember(null);
      return;
    }
    supabase
      .from("crew_members")
      .select("*")
      .eq("email", viewAsEmail.toLowerCase())
      .eq("is_active", true)
      .maybeSingle()
      .then(({ data }) => setViewAsMember(data || null));
  }, [viewAsEmail, realCrewMember?.role]);

  // ── Load this user's company feature flags (per-company tab gating) ─────────
  useEffect(() => {
    const orgId = realCrewMember?.organization_id;
    if (!orgId) { setCompanyFeatures(null); return; }
    supabase
      .from("companies")
      .select("features")
      .eq("id", orgId)
      .maybeSingle()
      .then(({ data }) => setCompanyFeatures(data?.features || {}))
      .catch(() => setCompanyFeatures(null));
  }, [realCrewMember?.organization_id]);

  const setViewAsEmail = useCallback((email) => {
    writeViewAs(email || "");
    setViewAsEmailState(email || "");
  }, []);

  if (realCrewMember === undefined) return <Spinner />;
  if (realCrewMember === null) return <AccessDenied signOut={signOut} reason={deniedReason} />;

  // ── Effective crew member (view-as OR real) ────────────────────────────────
  // Only honour view-as if the real user is an owner.
  const impersonating = realCrewMember.role === "owner" && !!viewAsEmail && !!viewAsMember;
  const crewMember = impersonating ? viewAsMember : realCrewMember;

  const role = crewMember.role || "crew";
  const permissions = crewMember.permissions || {};
  const isOwner = role === "owner";
  const isAdmin = role === "admin" || role === "owner";
  const jobberEmployeeName = crewMember.jobber_employee_name || null;

  // Always reflects the real signed-in user (used by Manage Crew & View-As banner)
  const realIsOwner = realCrewMember.role === "owner";

  function hasPermission(key) {
    if (isOwner || isAdmin) return true;
    return !!permissions[key];
  }

  // ── Per-company feature gating ─────────────────────────────────────────────
  // Browning sees everything; new companies are opt-in. Fail-open: while the
  // company features are still loading we show tabs (no flicker / accidental hide).
  const orgId = realCrewMember.organization_id || "browning-electric";
  const featuresLoaded = companyFeatures !== null;
  const isSuperadmin = !!realCrewMember.is_superadmin;
  function featureOn(key) {
    if (orgId === "browning-electric") return true;
    if (!featuresLoaded) return true;
    return companyFeatures[key] === true;
  }

  return (
    <PermissionsContext.Provider value={{
      role, permissions, isOwner, isAdmin, hasPermission, crewMember,
      jobberEmployeeName,
      realIsOwner, realCrewMember,
      viewAsEmail, setViewAsEmail, impersonating,
      orgId, isSuperadmin, companyFeatures, featureOn,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}
